import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type TFunction } from '@pkgs/components/providers/AppContextProvider';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { Modal, type ModalState } from '@ev-tech/ui-core-components/Modal';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';
import { alertVariants } from '@ev-tech/ui-core-components/Alert';
import { CrossNakedIcon as CrossNaked } from '@ev-tech/search-modules-shared/Icons';

import MessageLoading from '../../assets/icons/large/MessageLoading.svg';

import styles from './UnsubscribeDialog.module.css';

export const UnsubscribeDialog = ({
  modalState,
  onOpenChange,
  searchAlertInfo,
}: {
  modalState: ModalState;
  onOpenChange: (isOpen: boolean) => void;
  searchAlertInfo: { deleteAlert: string; authKey: string };
}) => {
  const {
    t,
    TransComponent,
    api,
    countryCode,
    options: { language },
  } = useAppContext();
  const { showMessage } = useStatusMessage();

  const { isOpen, close } = modalState;

  const {
    data: { data: searchAlertData } = {},
    isLoading,
    isError: isErrorLoadingAlertDetails,
  } = useQuery({
    queryKey: ['searchAlertInfo', searchAlertInfo.deleteAlert],
    queryFn: () =>
      api.getSearchAlertForDisplay(searchAlertInfo.deleteAlert.trim(), {
        authKey: searchAlertInfo.authKey,
      }),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.deleteSearchAlertPublic(searchAlertInfo.deleteAlert.trim(), {
        authKey: searchAlertInfo.authKey,
      }),
    onSuccess: () => {
      showMessage(t('search.userAccount.unsubscribe.modal.success'), { alertType: alertVariants.success });
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (isErrorLoadingAlertDetails) window.history.replaceState({}, '', window.location.pathname);
  }, [isErrorLoadingAlertDetails]);

  if (isErrorLoadingAlertDetails || isLoading) return null;

  return (
    <Modal initialFocus={false} modal data-testid="search-components_unsubscribe-dialog" open={isOpen} onOpenChange={onOpenChange}>
      <div className={styles.modal}>
        <div className={styles.topBar}>
          <IconButton icon={<CrossNaked />} label={t('search.common.close')} onClick={close} variant="tertiaryAlt" />
        </div>
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <MessageLoading />
            <Headline size="h3" as="h3" label={t('search.userAccount.unsubscribe.modal.header')} />
            <Copy
              size="l"
              text={
                <TransComponent
                  values={{ searchAlertName: `"${searchAlertData?.name}"` }}
                  components={{ b: <b className={styles.bold} key={searchAlertData?.name} /> }}
                  t={t as TFunction}
                  i18nKey="search.userAccount.unsubscribe.modal.subtitle1"
                />
              }
            />
            <Copy size="l" text={t('search.userAccount.unsubscribe.modal.subtitle2')} />
          </div>
          <div className={styles.modalButtons}>
            <Button
              size="medium"
              variant="secondary"
              loading={{ isLoading: isPending, label: '' }}
              label={isPending ? '' : t('search.userAccount.unsubscribe.modal.button.unsubscribe')}
              onClick={() => mutate()}
            />
            <Button
              size="medium"
              variant="tertiary"
              onClick={() => {
                location.href = `/${countryCode.toLowerCase()}/${language}/account/saved-searches`;
              }}
              label={t('search.userAccount.unsubscribe.modal.button.manage')}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
